import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GrnRow } from '@/components/grn/GrnRow';
import { GrnReasonDialog } from '@/components/grn/GrnReasonDialog';
import { grnApi, grnErr, money } from '@/services/grnApi';

const Pick = ({ value, onChange, items, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-40" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const Stat = ({ label, value, testId, tone = 'text-gray-900' }) => (
  <div className="rounded-lg border bg-white px-4 py-2"><p className="text-[11px] text-gray-500">{label}</p><p className={`text-lg font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</p></div>
);

/** Accounts: every GRN — filter, download the PDF, tick it booked; Admin can reopen or cancel. */
export const GrnList = ({ refreshKey, onChanged }) => {
  const [f, setF] = useState({ status: 'FINAL', booked: 'all', q: '', date_from: '', date_to: '' });
  const [data, setData] = useState(null);
  const [ask, setAsk] = useState(null);
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    grnApi.list(params).then(setData).catch((e) => { setData({ grns: [], summary: null }); toast.error(grnErr(e, 'Could not load GRNs')); });
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const booked = async (g, v) => {
    try { await grnApi.booked(g.grn_id, v); toast.success(`${g.grn_number} ${v ? 'booked' : 'marked not booked'}`); load(); } catch (e) { toast.error(grnErr(e, 'Could not update')); }
  };
  const s = data?.summary;
  return (
    <div className="space-y-3" data-testid="grn-list">
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input value={f.q} onChange={(e) => set('q')(e.target.value)} placeholder="GRN, supplier, invoice, delivery or PO no" className="h-10 pl-9" data-testid="grn-list-search" />
        </div>
        <Pick value={f.status} onChange={set('status')} testId="grn-list-status" items={[['FINAL', 'Final'], ['REOPENED', 'Reopened'], ['CANCELLED', 'Cancelled'], ['all', 'All status']]} />
        <Pick value={f.booked} onChange={set('booked')} testId="grn-list-booked" items={[['all', 'Booked or not'], ['no', 'Not booked'], ['yes', 'Booked']]} />
        <Input type="date" value={f.date_from} onChange={(e) => set('date_from')(e.target.value)} className="h-10 w-40" data-testid="grn-list-from" />
        <Input type="date" value={f.date_to} onChange={(e) => set('date_to')(e.target.value)} className="h-10 w-40" data-testid="grn-list-to" />
      </div>
      {s && <div className="flex flex-wrap gap-2">
        <Stat label="GRNs" value={s.count} testId="grn-sum-count" />
        <Stat label="Final value" value={money(s.value)} testId="grn-sum-value" />
        <Stat label="Not booked yet" value={`${s.unbooked} · ${money(s.unbooked_value)}`} tone="text-amber-700" testId="grn-sum-unbooked" />
      </div>}
      {!data ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-gray-400" />
        : !data.grns.length ? <p className="py-10 text-center text-sm text-gray-500" data-testid="grn-list-empty">No GRN matches these filters.</p> : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600"><tr>
                <th className="p-2 text-left">GRN</th><th className="p-2 text-left">Finalised</th><th className="p-2 text-left">Supplier</th>
                <th className="p-2 text-left">Delivery / POs</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Value</th>
                <th className="p-2 text-center">Accounts</th><th className="p-2" /></tr></thead>
              <tbody>{data.grns.map((g) => <GrnRow key={g.grn_id} g={g} onBooked={booked} onAsk={setAsk} />)}</tbody>
            </table>
          </div>)}
      <GrnReasonDialog ask={ask} onClose={() => setAsk(null)} onDone={() => { setAsk(null); load(); onChanged?.(); }} />
    </div>
  );
};

export default GrnList;
